import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import React from 'react';
import { Draggable } from 'react-beautiful-dnd';

import Button from 'uikit/Button';
import CleanedText from 'uikit/CleanedText';
import Icon from 'uikit/Icon';
import Menu, { MenuItem } from 'uikit/Menu';
import Tooltip from 'uikit/Tooltip';

import styles from './QuestionCard.module.scss';

const cx = classNames.bind(styles);

const QuestionCard = ({
  className,
  handleAnswersAggregate,
  handleExpertLinkCopy,
  handleQuestionClick,
  handleQuestionComments,
  handleQuestionDelete,
  handleQuestionDuplicate,
  handleQuestionMove,
  handleQuestionPreview,
  handleQuestionSelect,
  handleQuestionUpdate,
  handleStudentErrors,
  isExam,
  isSelected,
  me,
  nQuestionsSelected,
  position,
  question,
  questionInfo,
  t,
}) => (
  <Draggable draggableId={question.id} index={question.order}>
    {(providedQuestion, snapshotQuestion) => (
      // eslint-disable-next-line jsx-a11y/interactive-supports-focus, jsx-a11y/click-events-have-key-events
      <div
        className={cx(
          'container',
          {
            isDragging: snapshotQuestion.isDragging,
            isSelected,
          },
          className,
        )}
        ref={providedQuestion.innerRef}
        role="button"
        {...providedQuestion.draggableProps}
        {...providedQuestion.dragHandleProps}
      >
        <button
          className={cx('checkbox-container')}
          htmlFor={`checkbox${question.id}`}
          onClick={handleQuestionSelect}
          type="button"
        >
          <span
            className={cx('checkbox', {
              isChecked: isSelected,
            })}
          >
            <Icon name="check" />
          </span>
        </button>
        {snapshotQuestion.isDragging && nQuestionsSelected > 1 && (
          <div className={cx('number')}>{nQuestionsSelected}</div>
        )}
        <p className={cx('icon', `background-${questionInfo.color}`)}>
          {snapshotQuestion.isDragging ? (
            <Icon name="sort" />
          ) : (
            <img alt="" src={questionInfo.icon} />
          )}
        </p>
        <p className={cx('position')}>{position}.</p>
        <CleanedText
          as="p"
          className={cx('title')}
          data-testid={`question-card-${question.title}`}
          onClick={handleQuestionClick}
          role="button"
        >
          {question.title}
        </CleanedText>
        {question.author && question.author.id !== me.id && (
          <div className={cx('author')}>
            <Icon className={cx('author-icon')} name="user-graduate" />
            <span className={cx('author-name')}>
              {`${question.author.firstName || ''} ${
                question.author.lastName || ''
              }`}
            </span>
          </div>
        )}
        {isExam && !questionInfo.test && (
          <Tooltip
            tooltip={t(
              'Course/Teacher/Editor/Finder/QuestionCard/disabled-exam',
            )}
          >
            <Icon className={cx('disabled')} name="exclamation-triangle" />
          </Tooltip>
        )}
        {!isExam && !questionInfo.study && (
          <Tooltip
            tooltip={t(
              'Course/Teacher/Editor/Finder/QuestionCard/disabled-study',
            )}
          >
            <Icon className={cx('disabled')} name="exclamation-triangle" />
          </Tooltip>
        )}
        {question.comments.length > 0 && (
          <>
            <Button
              aria-label={t(
                'Course/Teacher/Editor/Finder/QuestionCard/aria-comments',
              )}
              className={cx('action')}
              icon="comment"
              size="small"
              onClick={handleQuestionComments}
            />
            {question.comments.some((comment) => !comment.isRead) && (
              <div className={cx('comment-notification')} />
            )}
          </>
        )}
        <Menu
          placement="bottom-end"
          toggle={
            <Button
              className={cx('action')}
              icon="ellipsis-v"
              size="small"
              tooltip={t('Course/Teacher/Editor/Finder/QuestionCard/options')}
            />
          }
        >
          <MenuItem icon="edit" onClick={handleQuestionUpdate}>
            {t('Course/Teacher/Editor/Finder/QuestionCard/update-question')}
          </MenuItem>
          <MenuItem icon="eye" onClick={handleQuestionPreview}>
            {t('Course/Teacher/Editor/Finder/QuestionCard/preview-question')}
          </MenuItem>
          <MenuItem icon="exclamation-square" onClick={handleStudentErrors}>
            {t('Course/Teacher/Editor/Finder/QuestionCard/student-errors')}
          </MenuItem>
          <MenuItem icon="seal-question" onClick={handleAnswersAggregate}>
            {t('Course/Teacher/Editor/Finder/QuestionCard/aggregated-answers')}
          </MenuItem>
          <MenuItem icon="arrow-square-right" onClick={handleQuestionMove}>
            {t('Course/Teacher/Editor/Finder/QuestionCard/move-question')}
          </MenuItem>
          <MenuItem icon="copy" onClick={handleQuestionDuplicate}>
            {t('Course/Teacher/Editor/Finder/QuestionCard/duplicate-question')}
          </MenuItem>
          {questionInfo.expert && (
            <MenuItem icon="user-tie" onClick={handleExpertLinkCopy}>
              {t('Course/Teacher/Editor/Finder/QuestionCard/copy-expert-link')}
            </MenuItem>
          )}
          <MenuItem
            icon="trash-alt"
            onClick={handleQuestionDelete}
            variant="regular"
          >
            {t('Course/Teacher/Editor/Finder/QuestionCard/delete-question')}
          </MenuItem>
        </Menu>
      </div>
    )}
  </Draggable>
);

QuestionCard.propTypes = {
  className: PropTypes.string,
  handleAnswersAggregate: PropTypes.func.isRequired,
  handleExpertLinkCopy: PropTypes.func.isRequired,
  handleQuestionClick: PropTypes.func.isRequired,
  handleQuestionComments: PropTypes.func.isRequired,
  handleQuestionDuplicate: PropTypes.func.isRequired,
  handleQuestionDelete: PropTypes.func.isRequired,
  handleQuestionMove: PropTypes.func.isRequired,
  handleQuestionPreview: PropTypes.func.isRequired,
  handleQuestionSelect: PropTypes.func.isRequired,
  handleQuestionUpdate: PropTypes.func.isRequired,
  handleStudentErrors: PropTypes.func.isRequired,
  isExam: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  me: PropTypes.object,
  nQuestionsSelected: PropTypes.number.isRequired,
  position: PropTypes.number.isRequired,
  question: PropTypes.object.isRequired,
  questionInfo: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

QuestionCard.defaultProps = {
  className: undefined,
  me: undefined,
};

export default React.memo(QuestionCard);
